// src/views/whatsapp/WhatsappConfig.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import whatsappService from "../../services/whatsappService";
import { useAuth } from "../../contexts/AuthContext";

const fmtDt = (d) => { try { return new Date(d).toLocaleString("pt-BR"); } catch { return d || "—"; } };

const csvEscape = (v) => {
  const s = v == null ? "" : String(v);
  return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const baixarCSV = (filename, header, rows) => {
  const lines = [header.map(csvEscape).join(";"), ...rows.map((r) => r.map(csvEscape).join(";"))];
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
};

const CFG_EMPTY = {
  urlEvolution: "http://localhost:8080", apiKey: "", instancia: "econdo",
  ativo: true,
  notifBoleto: true, notifBoletoVencido: true, notifLembrete3dias: true,
  notifReserva: true, notifOcorrencia: true, notifComunicado: true,
  notifRenegociacao: true, notifAssembleia: true,
};

const NOTIF_FLAGS = [
  { key: "notifBoleto",        label: "Boleto Gerado",            icon: "bi-receipt",            desc: "Envia quando um boleto é gerado para o condômino" },
  { key: "notifBoletoVencido", label: "Boleto Vencido",           icon: "bi-exclamation-circle", desc: "Alerta quando um boleto vence sem pagamento" },
  { key: "notifLembrete3dias", label: "Lembrete (3 dias antes)",  icon: "bi-bell",               desc: "Lembrete automático 3 dias antes do vencimento" },
  { key: "notifReserva",       label: "Reservas",                 icon: "bi-calendar-check",     desc: "Confirmação e cancelamento de reservas" },
  { key: "notifOcorrencia",    label: "Ocorrências",              icon: "bi-exclamation-triangle", desc: "Atualização de status da ocorrência" },
  { key: "notifComunicado",    label: "Comunicados",              icon: "bi-megaphone",          desc: "Comunicados publicados pelo condomínio" },
  { key: "notifRenegociacao",  label: "Renegociação",             icon: "bi-handshake",          desc: "Criação de acordo de renegociação" },
  { key: "notifAssembleia",    label: "Assembleias",              icon: "bi-people",             desc: "Convocação de assembleia com link de votação" },
];

// ── Banner ─────────────────────────────────────────────────────────────────
function Banner({ tipo = "info", children, onClose }) {
  const cores = {
    error:   { bg: "#FEE2E2", border: "#FECACA", color: "#991B1B", icon: "bi-exclamation-octagon" },
    success: { bg: "#DCFCE7", border: "#BBF7D0", color: "#166534", icon: "bi-check-circle" },
    info:    { bg: "#EFF6FF", border: "#BFDBFE", color: "#1D4ED8", icon: "bi-info-circle" },
    warning: { bg: "#FEF3C7", border: "#FDE68A", color: "#92400E", icon: "bi-exclamation-triangle" },
  };
  const c = cores[tipo] || cores.info;
  if (!children) return null;
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.color, borderRadius: 8,
      padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10, fontSize: 13.5,
    }}>
      <i className={`bi ${c.icon}`} style={{ fontSize: 18 }} />
      <div style={{ flex: 1 }}>{children}</div>
      {onClose && (
        <button className="btn btn-sm" style={{ padding: "2px 8px", background: "transparent", color: c.color }} onClick={onClose}>
          <i className="bi bi-x-lg" />
        </button>
      )}
    </div>
  );
}

function ConfirmModal({ titulo, mensagem, onConfirm, onCancel, perigo = false, textoOk = "Confirmar" }) {
  return (
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.5)" }} onClick={onCancel}>
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header"><h5 className="modal-title">{titulo}</h5>
            <button className="btn-close" onClick={onCancel} />
          </div>
          <div className="modal-body" style={{ fontSize: 14 }}>{mensagem}</div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onCancel}>Cancelar</button>
            <button className={`btn ${perigo ? "btn-danger" : "btn-primary"}`} onClick={onConfirm}>{textoOk}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WhatsappConfig() {
  const { isAdmin, isSindico, condominioAtivo, condominiosDisponiveis } = useAuth();
  const podeEditar = isAdmin || isSindico;

  const [aba, setAba] = useState("config");
  const [idCondo, setIdCondo] = useState("");
  const [form, setForm] = useState(CFG_EMPTY);
  const [temApiKey, setTemApiKey] = useState(false);
  const [apiKeyHint, setApiKeyHint] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [status, setStatus] = useState("Desconectado");
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const [testNr, setTestNr] = useState("");
  const [testMsg, setTestMsg] = useState("✅ Teste do Horizon eCondo — WhatsApp configurado com sucesso!");

  const [log, setLog] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logTotalPages, setLogTotalPages] = useState(1);
  const [logPage, setLogPage] = useState(1);
  const [filtroLog, setFiltroLog] = useState({ status: "", busca: "" });
  const [loadingLog, setLoadingLog] = useState(false);

  const [pageError, setPageError] = useState("");
  const [pageSuccess, setPageSuccess] = useState("");
  const [pageInfo, setPageInfo] = useState("");

  const showError   = (m) => { setPageError(m);   setPageSuccess(""); setPageInfo(""); };
  const showSuccess = (m) => { setPageSuccess(m); setPageError("");   setPageInfo(""); };
  const showInfo    = (m) => { setPageInfo(m);    setPageError("");   setPageSuccess(""); };

  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const condominiosLista = useMemo(() => condominiosDisponiveis || [], [condominiosDisponiveis]);

  useEffect(() => {
    if (condominioAtivo && !idCondo) setIdCondo(String(condominioAtivo.id));
  }, [condominioAtivo, idCondo]);

  // ── Carregar config / status ───────────────────────────────────────────
  const carregar = useCallback(async () => {
    if (!idCondo) return;
    try {
      const r = await whatsappService.buscarConfig(idCondo);
      const data = r.data || {};
      setForm({
        urlEvolution: data.urlEvolution || "http://localhost:8080",
        apiKey: "",
        instancia: data.instancia || "econdo",
        ativo: data.ativo !== undefined ? !!data.ativo : true,
        notifBoleto:        !!data.notifBoleto,
        notifBoletoVencido: !!data.notifBoletoVencido,
        notifLembrete3dias: !!data.notifLembrete3dias,
        notifReserva:       !!data.notifReserva,
        notifOcorrencia:    !!data.notifOcorrencia,
        notifComunicado:    !!data.notifComunicado,
        notifRenegociacao:  !!data.notifRenegociacao,
        notifAssembleia:    !!data.notifAssembleia,
      });
      setTemApiKey(!!data.temApiKey);
      setApiKeyHint(data.apiKeyHint || "");
      setWebhookSecret(data.webhookSecret || "");
      setStatus(data.status || "Desconectado");
    } catch (e) { showError(e.message); }
  }, [idCondo]);

  useEffect(() => { if (idCondo) carregar(); }, [idCondo, carregar]);

  // ── Polling de status (rodando sempre, mais frequente em "Aguardando") ──
  const pollRef = useRef(null);
  useEffect(() => {
    if (!idCondo) return;
    const interval = status === "Aguardando" ? 3000 : 15000;
    pollRef.current = setInterval(async () => {
      try {
        const r = await whatsappService.status(idCondo);
        const novoStatus = r.data?.status || "Desconectado";
        setStatus(novoStatus);
        if (novoStatus === "Conectado") setQrCode(null);
      } catch {}
    }, interval);
    return () => clearInterval(pollRef.current);
  }, [idCondo, status]);

  // ── Log ────────────────────────────────────────────────────────────────
  const carregarLog = useCallback(async () => {
    if (!idCondo) return;
    setLoadingLog(true);
    try {
      const r = await whatsappService.listarLog({
        idCondominio: idCondo, ...filtroLog, page: logPage, limit: 30,
      });
      setLog(r.data || []); setLogTotal(r.total || 0); setLogTotalPages(r.totalPages || 1);
    } catch (e) { showError(e.message); }
    setLoadingLog(false);
  }, [idCondo, filtroLog, logPage]);

  useEffect(() => { if (aba === "log" && idCondo) carregarLog(); /* eslint-disable-next-line */ }, [aba, idCondo, logPage]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleSalvar = async () => {
    if (!idCondo) return showError("Selecione um condomínio.");
    if (!form.urlEvolution?.trim()) return showError("Informe a URL da Evolution API.");
    if (!temApiKey && !form.apiKey?.trim()) return showError("Informe a API Key da Evolution.");
    setSaving(true);
    try {
      await whatsappService.salvarConfig({
        idCondominio: parseInt(idCondo),
        urlEvolution: form.urlEvolution.trim(),
        apiKey: form.apiKey?.trim() || undefined,
        instancia: form.instancia?.trim(),
        ativo: !!form.ativo,
        notifBoleto:        !!form.notifBoleto,
        notifBoletoVencido: !!form.notifBoletoVencido,
        notifLembrete3dias: !!form.notifLembrete3dias,
        notifReserva:       !!form.notifReserva,
        notifOcorrencia:    !!form.notifOcorrencia,
        notifComunicado:    !!form.notifComunicado,
        notifRenegociacao:  !!form.notifRenegociacao,
        notifAssembleia:    !!form.notifAssembleia,
      });
      setForm((p) => ({ ...p, apiKey: "" }));
      await carregar();
      showSuccess("Configuração salva com sucesso!");
    } catch (e) { showError(e.message); }
    setSaving(false);
  };

  const handleConectar = async () => {
    if (!idCondo) return showError("Selecione um condomínio.");
    setConnecting(true);
    setQrCode(null);
    try {
      const r = await whatsappService.conectar(idCondo);
      if (r.data?.qrCode) {
        setQrCode(r.data.qrCode);
        setStatus("Aguardando");
        showInfo("Escaneie o QR Code com o WhatsApp do celular do síndico.");
      } else if (r.data?.status === "Conectado") {
        setStatus("Conectado");
        showSuccess("WhatsApp já está conectado!");
      } else {
        showInfo(r.data?.mensagem || "Aguardando conexão.");
      }
    } catch (e) { showError(e.message); }
    setConnecting(false);
  };

  const handleDesconectar = async () => {
    setConfirmDisconnect(false);
    try {
      await whatsappService.desconectar(idCondo);
      setStatus("Desconectado"); setQrCode(null);
      showSuccess("WhatsApp desconectado.");
    } catch (e) { showError(e.message); }
  };

  const handleTestar = async () => {
    if (!testNr.trim()) return showError("Informe o número de WhatsApp.");
    setTesting(true);
    try {
      const r = await whatsappService.testar({
        idCondominio: parseInt(idCondo),
        nrWhatsApp: testNr.trim(),
        mensagem: testMsg,
      });
      showSuccess(r.message || "Mensagem enviada com sucesso!");
    } catch (e) { showError(e.message); }
    setTesting(false);
  };

  const handleReenviarLog = async (id) => {
    try {
      const r = await whatsappService.reenviarLog(id);
      showSuccess(r.message || "Reenviado.");
      carregarLog();
    } catch (e) { showError(e.message); }
  };

  const handleRegenerarSecret = async () => {
    try {
      const r = await whatsappService.regenerarSecret(idCondo);
      setWebhookSecret(r.webhookSecret || "");
      showSuccess("Webhook secret regenerado.");
    } catch (e) { showError(e.message); }
  };

  const copiar = async (txt, label = "Copiado!") => {
    try { await navigator.clipboard.writeText(txt); showInfo(label); }
    catch { showError("Falha ao copiar."); }
  };

  const exportarLogsCSV = () => {
    if (!log.length) return showError("Sem logs para exportar.");
    const header = ["Data/Hora", "Evento", "Destinatário", "Número", "Tipo", "Status", "Erro"];
    const rows = log.map((l) => [
      fmtDt(l.createdAt), l.tpNotificacao, l.nomeDestino || "—", l.nrDestino,
      l.tpMensagem, l.status, l.erroMsg || "",
    ]);
    baixarCSV(`whatsapp_log_${idCondo}_${new Date().toISOString().slice(0, 10)}.csv`, header, rows);
  };

  // ── Estilos ────────────────────────────────────────────────────────────
  const statusColor = { Conectado: "#166534", Desconectado: "#991B1B", Aguardando: "#C2410C" };
  const statusBg    = { Conectado: "#DCFCE7", Desconectado: "#FEE2E2", Aguardando: "#FFF7ED" };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div>
          <h1 className="page-title">
            <i className="bi bi-whatsapp me-2" style={{ color: "#25D366", fontSize: 22 }} />
            WhatsApp
          </h1>
          <p className="page-subtitle">Notificações automáticas via WhatsApp (Evolution API) — por condomínio</p>
        </div>
        {idCondo && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: statusBg[status] || "#FEE2E2", borderRadius: 20, padding: "8px 16px",
          }}>
            <div style={{
              width: 10, height: 10, borderRadius: "50%",
              background: statusColor[status] || "#991B1B",
              animation: status === "Aguardando" ? "pulse 1.5s infinite" : "none",
            }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: statusColor[status] || "#991B1B" }}>{status}</span>
            {status === "Conectado" && podeEditar && (
              <button className="btn btn-sm" style={{
                fontSize: 11, padding: "2px 8px",
                background:"var(--bg-danger-soft)", color: "#991B1B", border: "none",
              }} onClick={() => setConfirmDisconnect(true)}>Desconectar</button>
            )}
          </div>
        )}
      </div>

      {/* Seletor de condomínio */}
      <div className="form-section" style={{ marginBottom: 12 }}>
        <div className="row g-3 align-items-center">
          <div className="col-md-4">
            <label className="form-label">Condomínio</label>
            <select className="form-select" value={idCondo} onChange={(e) => { setIdCondo(e.target.value); setQrCode(null); }}>
              <option value="">— Selecione —</option>
              {condominiosLista.map((c) => (
                <option key={c.id} value={c.id}>{c.nmFantasia || c.nmCondominio}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <Banner tipo="error"   onClose={() => setPageError("")}>{pageError}</Banner>
      <Banner tipo="success" onClose={() => setPageSuccess("")}>{pageSuccess}</Banner>
      <Banner tipo="info"    onClose={() => setPageInfo("")}>{pageInfo}</Banner>

      {!idCondo ? (
        condominiosLista.length === 0 ? (
          <Banner tipo="warning">
            Nenhum condomínio disponível. {isAdmin ? "Cadastre um condomínio primeiro." : "Contate o administrador para liberar acesso."}
          </Banner>
        ) : (
          <div style={{ textAlign: "center", padding: 48, color: "var(--gray-400)" }}>
            <i className="bi bi-whatsapp" style={{ fontSize: 48, color: "#25D366" }} />
            <p style={{ marginTop: 16 }}>Selecione um condomínio para gerenciar o WhatsApp</p>
          </div>
        )
      ) : (
        <>
          {!podeEditar && (
            <Banner tipo="warning">Você está em modo somente leitura. Operações de escrita exigem perfil Admin ou Síndico.</Banner>
          )}

          {/* Abas */}
          <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid var(--gray-200)" }}>
            {[
              { id: "config", icon: "bi-gear",    label: "Configuração" },
              { id: "notif",  icon: "bi-bell",    label: "Notificações" },
              { id: "testar", icon: "bi-send",    label: "Testar" },
              { id: "log",    icon: "bi-list-ul", label: "Log de Envios" },
            ].map((t) => (
              <button key={t.id} onClick={() => setAba(t.id)} style={{
                padding: "8px 16px", border: "none", background: "transparent", cursor: "pointer",
                fontSize: 13, fontWeight: aba === t.id ? 700 : 400,
                color: aba === t.id ? "#25D366" : "var(--gray-500)",
                borderBottom: aba === t.id ? "2px solid #25D366" : "2px solid transparent", marginBottom: -1,
              }}>
                <i className={`bi ${t.icon} me-1`} />{t.label}
              </button>
            ))}
          </div>

          {/* ══ ABA: CONFIGURAÇÃO ══ */}
          {aba === "config" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 980 }}>
              <div style={{ background:"var(--bg-surface)", borderRadius: 12, padding: 24, border: "1px solid var(--gray-200)" }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "var(--gray-800)", marginBottom: 16 }}>
                  Evolution API — Conexão
                </div>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">URL da Evolution API *</label>
                    <input className="form-control" value={form.urlEvolution}
                      disabled={!podeEditar}
                      onChange={(e) => set("urlEvolution", e.target.value)}
                      placeholder="http://localhost:8080" />
                    <small style={{ fontSize: 11, color: "var(--gray-400)" }}>
                      URL onde a Evolution API está rodando (acessível pelo backend).
                    </small>
                  </div>
                  <div className="col-12">
                    <label className="form-label">
                      API Key {!temApiKey && "*"}
                      {temApiKey && <span className="ms-2 text-muted" style={{ fontSize: 11 }}>(atual termina em <code>•••{apiKeyHint}</code>)</span>}
                    </label>
                    <div className="input-group">
                      <input className="form-control" type={showApiKey ? "text" : "password"}
                        disabled={!podeEditar}
                        value={form.apiKey}
                        onChange={(e) => set("apiKey", e.target.value)}
                        placeholder={temApiKey ? "Deixe em branco para manter atual" : "Chave de autenticação"} />
                      <button className="btn btn-outline-secondary" type="button" onClick={() => setShowApiKey((v) => !v)}>
                        <i className={`bi bi-${showApiKey ? "eye-slash" : "eye"}`} />
                      </button>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Nome da Instância</label>
                    <input className="form-control" value={form.instancia}
                      disabled={!podeEditar}
                      onChange={(e) => set("instancia", e.target.value)}
                      placeholder="econdo" />
                    <small style={{ fontSize: 11, color: "var(--gray-400)" }}>
                      Use um nome único por condomínio (ex.: <code>condo-{idCondo}</code>) para evitar conflitos na Evolution.
                    </small>
                  </div>
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox"
                        disabled={!podeEditar}
                        checked={!!form.ativo}
                        onChange={(e) => set("ativo", e.target.checked)} />
                      <label className="form-check-label">Integração ativa</label>
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
                  {podeEditar && (
                    <button className="btn btn-primary" onClick={handleSalvar} disabled={saving}>
                      {saving ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-save me-1" />Salvar</>}
                    </button>
                  )}
                  {podeEditar && status !== "Conectado" && (
                    <button className="btn btn-success" onClick={handleConectar} disabled={connecting || !temApiKey}>
                      {connecting ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-phone me-1" />Conectar WhatsApp</>}
                    </button>
                  )}
                </div>

                {/* Webhook secret */}
                {webhookSecret && (
                  <div style={{ marginTop: 16, background: "#FEF3C7", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                      <i className="bi bi-key me-1" />Webhook Secret
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <code style={{ fontSize: 11, wordBreak: "break-all", flex: 1 }}>{webhookSecret}</code>
                      <button className="btn btn-sm btn-outline-warning" onClick={() => copiar(webhookSecret, "Secret copiado!")}>
                        <i className="bi bi-clipboard" />
                      </button>
                      {isAdmin && (
                        <button className="btn btn-sm btn-outline-danger" onClick={handleRegenerarSecret} title="Regenerar">
                          <i className="bi bi-arrow-clockwise" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* QR Code / Status */}
              <div style={{
                background:"var(--bg-surface)", borderRadius: 12, padding: 24, border: "1px solid var(--gray-200)",
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12,
              }}>
                {status === "Conectado" ? (
                  <>
                    <div style={{
                      width: 80, height: 80, borderRadius: "50%", background:"var(--bg-success-soft)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <i className="bi bi-check-circle-fill" style={{ fontSize: 40, color: "#166534" }} />
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: "#166534" }}>WhatsApp Conectado!</div>
                    <div style={{ fontSize: 13, color: "var(--gray-500)", textAlign: "center" }}>
                      Notificações automáticas estão ativas.<br />
                      Configure quais eventos disparar na aba Notificações.
                    </div>
                  </>
                ) : qrCode ? (
                  <>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "var(--gray-800)", marginBottom: 4 }}>
                      Escaneie o QR Code
                    </div>
                    <img src={qrCode} alt="QR Code WhatsApp"
                      style={{ width: 220, height: 220, borderRadius: 12, border: "2px solid #25D366" }} />
                    <div style={{ fontSize: 12, color: "var(--gray-500)", textAlign: "center" }}>
                      Abra o WhatsApp no celular → Menu → Aparelhos Conectados → Conectar Aparelho
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#C2410C" }}>
                      <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} />
                      Aguardando conexão...
                    </div>
                  </>
                ) : (
                  <>
                    <i className="bi bi-qr-code" style={{ fontSize: 60, color: "var(--gray-300)" }} />
                    <div style={{ fontWeight: 600, fontSize: 14, color: "var(--gray-600)" }}>Não conectado</div>
                    <div style={{ fontSize: 12, color: "var(--gray-400)", textAlign: "center" }}>
                      Salve a configuração e clique em<br />"Conectar WhatsApp"
                    </div>
                  </>
                )}
              </div>

              {/* Instruções */}
              <div style={{
                gridColumn: "1/-1", background:"var(--bg-success-soft)", borderRadius: 12, padding: 16,
                border: "1px solid #BBF7D0", fontSize: 13,
              }}>
                <div style={{ fontWeight: 700, color: "#166534", marginBottom: 8 }}>
                  <i className="bi bi-info-circle me-2" />Como configurar a Evolution API
                </div>
                <ol style={{ margin: 0, paddingLeft: 20, color: "#166534", lineHeight: 1.8 }}>
                  <li>Instale a Evolution API: <code>docker run -p 8080:8080 atendai/evolution-api</code></li>
                  <li>Acesse <strong>http://localhost:8080/manager</strong> e crie uma instância</li>
                  <li>Copie a API Key gerada e cole no campo acima</li>
                  <li>Clique em "Conectar WhatsApp" e escaneie o QR Code com o celular do síndico</li>
                  <li>Pronto! As notificações serão enviadas automaticamente para os condôminos deste condomínio</li>
                </ol>
              </div>
            </div>
          )}

          {/* ══ ABA: NOTIFICAÇÕES ══ */}
          {aba === "notif" && (
            <div style={{ background:"var(--bg-surface)", borderRadius: 12, padding: 24, border: "1px solid var(--gray-200)", maxWidth: 600 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "var(--gray-800)", marginBottom: 4 }}>
                Eventos de Notificação
              </div>
              <p style={{ fontSize: 13, color: "var(--gray-500)", marginBottom: 16 }}>
                Selecione quais eventos devem disparar mensagens no WhatsApp para condôminos deste condomínio.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {NOTIF_FLAGS.map((n) => (
                  <label key={n.key} style={{
                    display: "flex", alignItems: "center", gap: 12, cursor: podeEditar ? "pointer" : "not-allowed",
                    padding: "12px 14px", borderRadius: 10, border: "1px solid var(--gray-200)",
                    background: form[n.key] ? "#F0FDF4" : "#fff", opacity: podeEditar ? 1 : 0.7,
                    transition: "all .15s",
                  }}>
                    <div style={{
                      width: 44, height: 26, borderRadius: 13,
                      background: form[n.key] ? "#25D366" : "var(--gray-200)",
                      position: "relative", transition: "background .2s", flexShrink: 0, cursor: podeEditar ? "pointer" : "not-allowed",
                    }} onClick={() => podeEditar && set(n.key, !form[n.key])}>
                      <div style={{
                        position: "absolute", top: 3,
                        left: form[n.key] ? 20 : 3, width: 20, height: 20, borderRadius: "50%",
                        background:"var(--bg-surface)", transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)",
                      }} />
                    </div>
                    <i className={`bi ${n.icon}`} style={{ color: form[n.key] ? "#25D366" : "var(--gray-400)", fontSize: 18 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "var(--gray-800)" }}>{n.label}</div>
                      <div style={{ fontSize: 11, color: "var(--gray-400)" }}>{n.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              {podeEditar && (
                <button className="btn btn-primary mt-4" onClick={handleSalvar} disabled={saving}>
                  {saving ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-save me-1" />}
                  Salvar Preferências
                </button>
              )}
            </div>
          )}

          {/* ══ ABA: TESTAR ══ */}
          {aba === "testar" && (
            <div style={{ background:"var(--bg-surface)", borderRadius: 12, padding: 24, border: "1px solid var(--gray-200)", maxWidth: 500 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "var(--gray-800)", marginBottom: 16 }}>
                Enviar Mensagem de Teste
              </div>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Número de WhatsApp</label>
                  <div className="input-group">
                    <span className="input-group-text" style={{ fontSize: 13 }}>+55</span>
                    <input className="form-control" value={testNr}
                      onChange={(e) => setTestNr(e.target.value)}
                      placeholder="11999999999 (com DDD, sem espaços)" />
                  </div>
                  <small style={{ fontSize: 11, color: "var(--gray-400)" }}>
                    Formato: DDD + número (ex: 11999999999)
                  </small>
                </div>
                <div className="col-12">
                  <label className="form-label">Mensagem</label>
                  <textarea className="form-control" rows={3} value={testMsg}
                    onChange={(e) => setTestMsg(e.target.value)} />
                </div>
              </div>
              <button className="btn btn-success mt-3" onClick={handleTestar}
                disabled={testing || status !== "Conectado" || !podeEditar}>
                {testing ? <><span className="spinner-border spinner-border-sm me-2" />Enviando...</>
                  : <><i className="bi bi-whatsapp me-2" />Enviar Mensagem de Teste</>}
              </button>
              {status !== "Conectado" && (
                <p style={{ fontSize: 12, color: "#C2410C", marginTop: 8 }}>
                  <i className="bi bi-exclamation-triangle me-1" />
                  WhatsApp não conectado. Conecte primeiro na aba Configuração.
                </p>
              )}
            </div>
          )}

          {/* ══ ABA: LOG ══ */}
          {aba === "log" && (
            <div style={{ background:"var(--bg-surface)", borderRadius: 12, border: "1px solid var(--gray-200)", overflow: "hidden" }}>
              <div style={{
                padding: "14px 18px", borderBottom: "1px solid var(--gray-100)",
                display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
              }}>
                <select className="form-select form-select-sm" style={{ width: "auto" }}
                  value={filtroLog.status}
                  onChange={(e) => { setFiltroLog((p) => ({ ...p, status: e.target.value })); setLogPage(1); }}>
                  <option value="">Todos os status</option>
                  <option value="Enviado">Enviados</option>
                  <option value="Erro">Com erro</option>
                  <option value="Pendente">Pendentes</option>
                </select>
                <input className="form-control form-control-sm" placeholder="Buscar nome/número/evento"
                  style={{ width: 240 }}
                  value={filtroLog.busca}
                  onChange={(e) => setFiltroLog((p) => ({ ...p, busca: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && (setLogPage(1), carregarLog())} />
                <button className="btn btn-outline-primary btn-sm" onClick={() => { setLogPage(1); carregarLog(); }}>
                  <i className="bi bi-search" />
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={carregarLog} title="Atualizar">
                  <i className="bi bi-arrow-clockwise" />
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={exportarLogsCSV} disabled={!log.length} title="Exportar CSV">
                  <i className="bi bi-file-earmark-spreadsheet me-1" />CSV
                </button>
                <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--gray-500)" }}>
                  {logTotal} registro(s) • página {logPage} de {logTotalPages}
                </span>
              </div>
              {loadingLog ? (
                <div style={{ textAlign: "center", padding: 40 }}><span className="spinner-border text-primary" /></div>
              ) : !log.length ? (
                <div style={{ textAlign: "center", padding: 40, color: "var(--gray-400)" }}>
                  <i className="bi bi-whatsapp" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                  <p>Nenhum envio registrado</p>
                </div>
              ) : (
                <>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "var(--gray-50)" }}>
                        {["Evento", "Destinatário", "Número", "Tipo", "Status", "Data", "Erro", "Ações"].map((h) => (
                          <th key={h} style={{
                            padding: "10px 14px", textAlign: "left",
                            fontWeight: 600, color: "var(--gray-600)", fontSize: 12,
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {log.map((l, i) => (
                        <tr key={l.id} style={{
                          borderBottom: "1px solid var(--gray-100)",
                          background: i % 2 === 0 ? "var(--bg-surface)" : "var(--bg-surface-alt)",
                        }}>
                          <td style={{ padding: "9px 14px", fontSize: 13 }}>{l.tpNotificacao}</td>
                          <td style={{ padding: "9px 14px", fontSize: 13 }}>{l.nomeDestino || "—"}</td>
                          <td style={{ padding: "9px 14px", fontSize: 12, fontFamily: "monospace" }}>{l.nrDestino}</td>
                          <td style={{ padding: "9px 14px" }}>
                            <span style={{ fontSize: 11 }}>{l.tpMensagem}</span>
                          </td>
                          <td style={{ padding: "9px 14px" }}>
                            <span style={{
                              background: l.status === "Enviado" ? "#DCFCE7" : l.status === "Erro" ? "#FEE2E2" : "#FEF9C3",
                              color:      l.status === "Enviado" ? "#166534" : l.status === "Erro" ? "#991B1B" : "#854D0E",
                              fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                            }}>{l.status}</span>
                          </td>
                          <td style={{ padding: "9px 14px", fontSize: 12, color: "var(--gray-500)" }}>{fmtDt(l.createdAt)}</td>
                          <td style={{
                            padding: "9px 14px", fontSize: 12, color: "#991B1B", maxWidth: 200,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>{l.erroMsg || "—"}</td>
                          <td style={{ padding: "9px 14px" }}>
                            {l.status === "Erro" && podeEditar && (
                              <button className="btn btn-outline-primary btn-sm btn-icon" title="Reenviar"
                                onClick={() => handleReenviarLog(l.id)}>
                                <i className="bi bi-arrow-repeat" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {logTotalPages > 1 && (
                    <div className="d-flex justify-content-center align-items-center gap-2 my-3">
                      <button className="btn btn-sm btn-outline-secondary" disabled={logPage <= 1} onClick={() => setLogPage(1)}>«</button>
                      <button className="btn btn-sm btn-outline-secondary" disabled={logPage <= 1} onClick={() => setLogPage((p) => Math.max(1, p - 1))}>‹</button>
                      <span style={{ fontSize: 13 }}>{logPage} / {logTotalPages}</span>
                      <button className="btn btn-sm btn-outline-secondary" disabled={logPage >= logTotalPages} onClick={() => setLogPage((p) => Math.min(logTotalPages, p + 1))}>›</button>
                      <button className="btn btn-sm btn-outline-secondary" disabled={logPage >= logTotalPages} onClick={() => setLogPage(logTotalPages)}>»</button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {confirmDisconnect && (
        <ConfirmModal titulo="Desconectar WhatsApp" perigo textoOk="Desconectar"
          mensagem="Tem certeza que deseja desconectar o WhatsApp deste condomínio? As notificações deixarão de ser enviadas até que reconecte."
          onConfirm={handleDesconectar} onCancel={() => setConfirmDisconnect(false)} />
      )}

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
    </div>
  );
}
